import { Component, type OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';

import { DashboardService } from 'src/app/service/events/dashboard.service';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { TareasApiService } from 'src/app/service/api/tareas-api.service';
import { VentasApiService } from 'src/app/service/api/ventas-api.service';
import { VisitasApiService } from 'src/app/service/api/visitas-api.service';
import { UserActivityApiService } from 'src/app/service/api/user_activity-api.service';

import { environment } from 'src/environments/environment';
import type { UserActivityModel } from 'src/app/models/usuarios/user-activity.model';
import { RequestHandler } from 'src/app/service/OffService/request-handler';

type ChartType = 'line' | 'bar' | 'doughnut' | 'pie';

interface CommonChartOptions {
  field: string;
  label: string;
  extraFields?: string[];
  fieldIsArray: boolean;
  multipleFields: boolean;
}

interface ChartCfg<T = any> {
  title: string;
  request: RequestHandler<T>;     // <- same type as <app-common-chart [request]>
  type: ChartType;
  options: CommonChartOptions;    // <- same shape as <app-common-chart [options]>
  colClass: string;
  when?: () => boolean;           // optional visibility guard
}


@Component({
  selector: 'app-statistics',
  standalone: false,
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
})
export class StatisticsComponent implements OnInit {
  public readonly hasClients   = environment.features.hasClients;
  
  public readonly tieneVisitas = environment.features.showVisitas;
  
  public readonly showVentas   = environment.features.showVentas;

  public readonly showFitoFechaReceta = environment.features.showFitoFechaReceta;
  
  public readonly showTareasMto = environment.features.showTareasMto;

  public userActivities: UserActivityModel[] = [];
  
  public userActivitiesFormat: { msg: string; request_method: string; fecha: string }[] = [];

  
  // API requests (reused in chart configs)
  public readonly requestTareasFito      = this.tareasApi.fitosanitariosGET;
  
  public readonly requestVentas          = this.ventasApi.ventasGET;
  
  public readonly requestVisitas         = this.visitasApi.visitasESTADISTICAS_GET;
  
  public readonly requestFitosanitarias  = this.tareasApi.fitosanitariosGET;
  
  public readonly requestFertilizantes   = this.tareasApi.fertilizantes.GET;

  // Chart options (shared)
  public readonly optionsTareasFito: CommonChartOptions = {
    field: 'id_tratamiento',
    label: 'nombre',
    fieldIsArray: true,
    multipleFields: true,
  };

  public readonly optionsVentas: CommonChartOptions = {
    field: 'cantidad',
    label: 'cliente',
    fieldIsArray: true,
    multipleFields: true,
  };

  public readonly optionsVisitas: CommonChartOptions = {
    field: 'cantidad',
    label: 'cliente',
    fieldIsArray: true,
    multipleFields: true,
  };

  /** Render plan for all charts (DRY) */
  public charts: ChartCfg[] = [];

  constructor(
    private readonly dashboard: DashboardService,
    private readonly router: Router,
    private readonly userActivityApi: UserActivityApiService,
    private readonly ventasApi: VentasApiService,
    private readonly tareasApi: TareasApiService,
    private readonly visitasApi: VisitasApiService
  ) {
    void dashboard;
  }

  async ngOnInit(): Promise<void> {
    // Guard non-admins
    if (StorageManager.getUser().rol !== 'admin') {
      this.router.navigate(['dashboard', 'inicio']);
      return;
    }
    this.dashboard.setMenuItem('statistics');

    // Build chart list once dependencies are ready
    this.charts = [
      {
        title: 'Consumo de Fertilizantes',
        request: this.requestFertilizantes,
        type: 'line',
        options: {
          field: 'dosis_total',
          label: 'fecha_inicio',
          extraFields: ['sup_cultivada'],
          fieldIsArray: false,
          multipleFields: false,
        },
        colClass: 'col-lg-6 col-md-12',
      },
      {
        title: 'Consumo de Fitosanitarios',
        request: this.requestFitosanitarias,
        type: 'line',
        options: {
          field: 'litros',
          label: 'fecha_inicio',
          fieldIsArray: false,
          multipleFields: false,
        },
        colClass: 'col-lg-6 col-md-12',
        when: (): boolean => this.showFitoFechaReceta && !this.showTareasMto,
      },
      {
        title: 'Productos fito más utilizados',
        request: this.requestTareasFito,
        type: 'doughnut',
        options: this.optionsTareasFito,
        colClass: 'col-lg-3 col-md-12',
      },
      {
        title: 'Ventas por finca y comprador',
        request: this.requestVentas,
        type: 'bar',
        options: {
          field: 'cantidad',
          label: 'finca',
          fieldIsArray: false,
          multipleFields: true,
        },
        colClass: 'col-lg-6 col-md-12',
        when: (): boolean => this.showVentas,
      },
      {
        title: 'Clientes con más visitas',
        request: this.requestVisitas,
        type: 'pie',
        options: this.optionsVisitas,
        colClass: 'col-lg-3 col-md-12',
        when: (): boolean => this.tieneVisitas && this.hasClients,
      },
    ];

    // Load activities
    try {
        this.userActivities = await this.userActivityApi.statisticsGET.toPromise();
        this.formatUserActivities();
    } catch (e) {
        console.log('statisticsGET error:', e);
    }

  }

  /** Map raw activities to a view-friendly model */
  public formatUserActivities(): void {
    this.userActivitiesFormat = this.userActivities.map(it => {
      // 1) Message prefix by verb
      let msgStart = 'Se ha estado activo recientemente ';

      try {
        const data = it.data ? JSON.parse(it.data) : {};

        if (it.request_method === 'POST')  {msgStart = 'Se ha añadido un registro ';}
        if (it.request_method === 'PUT')   {msgStart = `Se ha editado el registro #${data?.id ?? ''} `;}
        if (it.request_method === 'DELETE'){msgStart = `Se ha borrado el registro #${data?.id ?? ''} `;}
      } catch {
        // ignore parse errors → keep default text
      }

      // 2) Table normalized (replace all underscores)
      const table = String(it.affected_table ?? '')
        .replace(/_/g, '\xa0')
        .toUpperCase();

      // 3) Time string (today → relative; else formatted)
      const isToday = moment(it.fecha).format('DD/MM/YYYY') === moment().format('DD/MM/YYYY');
      const fecha = isToday
        ? moment(it.fecha).startOf('second').fromNow()
        : moment(it.fecha).format('DD [de] MMMM [a las] H:mm');

      return {
        msg: `${msgStart} en ${table}.`,
        request_method: it.request_method,
        fecha,
      };
    });
  }

  /** CSS class by method */
  public getActivityClass(method: string): string {
    switch ((method ?? '').toUpperCase()) {
      case 'POST':   return 'is-post';
      case 'PUT':    return 'is-put';
      case 'DELETE': return 'is-delete';
      default:       return 'is-default';
    }
  }

  /** TrackBy for activities list */
  public activityTrackBy = (_: number, a: { msg: string; request_method: string; fecha: string }): string =>
    `${a.request_method}|${a.msg}|${a.fecha}`;
}